import { setError, setLoaded, setLoading, withCallState, withLocalStorage, withStorageSync } from '@angular-architects/ngrx-toolkit';
import { inject, Injectable, linkedSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CustomerModel } from '@libs/account/models';
import { patchState, signalStore, withComputed, withLinkedState, withMethods, withProps, withState } from '@ngrx/signals';
import { firstValueFrom, Observable } from 'rxjs';
import { CustomerRepositoryService } from '../customer';
import { NoPermittedCustomersError } from './no-permitted-customers.error';

/* Customers */
interface CustomersStateModel {
  customers: CustomerModel[] | undefined;
  currentCustomerId: string | undefined;
}

const initialCustomersState: CustomersStateModel = {
  customers: undefined,
  currentCustomerId: undefined,
};

@Injectable({ providedIn: 'root' })
export class CustomersState extends signalStore(
  withState(initialCustomersState),
  withCallState(),
  withStorageSync({
    key: 'cepCustomerStorage',
    select: ({ currentCustomerId }) => ({ currentCustomerId }),
  }, withLocalStorage()),
  withProps(() => ({
    customerRepository: inject(CustomerRepositoryService),
  })),
  withMethods(store => ({
    isCustomerBlocked: (customer?: CustomerModel): boolean => {
      return store.customerRepository.isCustomerBlocked(customer);
    },
  })),
  withComputed(({ customers, currentCustomerId }) => ({
    currentCustomer: linkedSignal<
      { customers: CustomerModel[] | undefined; currentCustomerId: string | undefined },
      CustomerModel | undefined
    >({
      source: () => ({
        customers: customers(),
        currentCustomerId: currentCustomerId(),
      }),
      computation: ({ customers, currentCustomerId }) => {
        if (!customers) {
          return undefined;
        }

        let customer: CustomerModel | undefined = undefined;

        if (currentCustomerId) {
          customer = customers.find((c) => c.id === currentCustomerId);
        }

        return customer ?? customers[0];
      },
    }),
  })),
  withLinkedState((store) => ({
    isCurrentCustomerBlocked: linkedSignal<CustomerModel | undefined, boolean>({
      source: store.currentCustomer,
      computation: (currentCustomer) => {
        return store.customerRepository.isCustomerBlocked(currentCustomer);
      },
    }),
  })),
  withProps(store => ({
    _allCustomers$: toObservable(store.customers),
    _currentCustomer$: toObservable(store.currentCustomer),
  })),
  withComputed(store => ({
    customersLoaded(): boolean {
      return Boolean(store.customers()?.length);
    },
  })),
  withMethods(store => ({
    _fetchCustomer: async (customerId: string): Promise<CustomerModel[]> => {
      const fetchedCustomer: CustomerModel = await firstValueFrom(store.customerRepository.getByNumber(customerId));

      const customers: CustomerModel[] = store.customers() ?? [];

      if (!customers.length) {
        return [];
      }

      const currentCustomerIndex: number = customers.findIndex(
        (item: CustomerModel) => item.id === fetchedCustomer.id,
      );

      customers[currentCustomerIndex] = fetchedCustomer;

      return customers;
    },
  })),
  withMethods(store => ({
    clear: (): void => {
      patchState(store, { customers: undefined });
    },
    // only for use in storybook
    setCustomersRaw: (customers: CustomerModel[]): void => {
      patchState(store, { customers: customers, currentCustomerId: customers[0].id });
    },
    setCurrentCustomer: (customer?: CustomerModel): void => {
      const currentCustomer: CustomerModel | undefined
        = customer && store.customers()?.includes(customer) ? customer : undefined;

      patchState(store, { currentCustomerId: currentCustomer?.id });
    },
    loadCustomers: async (): Promise<void> => {
      patchState(store, setLoading());

      return firstValueFrom(store.customerRepository.readAll())
        .then(customers => {
          const result: CustomerModel[]
            = customers?.filter(customer => customer.blockingStatus !== 'fullyBlocked')
            .sort((a: CustomerModel, b: CustomerModel) => a.id.localeCompare(b.id));

          if (!result?.length) {
            return Promise.reject(new NoPermittedCustomersError());
          }

          const currentId: string | undefined = store.currentCustomerId();
          const current: CustomerModel = result.find(x => x.id === currentId) ?? result[0];

          patchState(store, { customers: result, currentCustomerId: current.id }, setLoaded());

          return Promise.resolve();
        })
        .catch(error => {
          patchState(store, { customers: undefined }, setLoaded(), setError(error));
          console.error(error);

          return Promise.reject(error as Error);
        });
    },
    loadCustomerByNumber: (customerNumber: string): Promise<void> => {
      patchState(store, setLoading());

      return firstValueFrom(store.customerRepository.getByNumber(customerNumber))
        .then(customer => {
          patchState(store, { customers: [customer], currentCustomerId: customer.id }, setLoaded());
        })
        .catch(error => {
          patchState(store, { customers: undefined }, setLoaded(), setError(error));
          console.error(error);

          return Promise.reject(error as Error);
        });
    },
    getAllCustomers: (): Observable<CustomerModel[] | undefined> => {
      return store._allCustomers$;
    },
    getCurrentCustomer: (): Observable<CustomerModel | undefined> => {
      return store._currentCustomer$;
    },
    updateCustomer: async (data: Partial<CustomerModel>): Promise<void> => {
      if (!data.id) {
        return Promise.reject(new Error('Customer has no id.'));
      }

      const updatedCustomer: CustomerModel | undefined = await firstValueFrom(store.customerRepository.update(data.id, data));

      const customers: CustomerModel[] = store.customers() ?? [];

      if (!customers.length) {
        return;
      }

      const currentCustomerIndex: number = customers.findIndex(
        (item: CustomerModel) => item.id === updatedCustomer.id,
      );

      customers[currentCustomerIndex] = updatedCustomer;

      patchState(store, { customers: customers });
    },
    fetchCustomer: async (customer: CustomerModel): Promise<CustomerModel | undefined> => {
      patchState(store, setLoading());

      try {
        const updatedCustomers: CustomerModel[] = await store._fetchCustomer(customer.id);

        patchState(store, { customers: updatedCustomers }, setLoaded());

        return updatedCustomers.find(x => x.id === customer.id);
      } catch (error: unknown) {
        patchState(store, setLoaded(), setError(error));
        console.error(error);

        return Promise.reject(error instanceof Error ? error : new Error(String(error)));
      }
    },
    changeCustomer: async (customer: CustomerModel): Promise<void> => {
      patchState(store, setLoading());

      const updatedCustomers: CustomerModel[] = await store._fetchCustomer(customer.id);
      const currentCustomer: CustomerModel | undefined
        = updatedCustomers.find(c => c.id === customer.id);

      patchState(store, {
        customers: updatedCustomers,
        currentCustomerId: currentCustomer?.id,
      }, setLoaded());
    },
  })),
) {
}
